import React from 'react';
import { Link } from 'react-router-dom';
import { Flame, Search, Sparkles } from 'lucide-react';

interface HomeSearchSectionProps {
  searchQuery: string;
  resultCount: number;
  onSearchInput: (value: string) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}

const popularSearches = [
  { label: 'LG Washers', query: 'LG washer' },
  { label: 'Samsung Dryers', query: 'Samsung dryer' },
  { label: 'Energy Star', query: 'Energy Star' },
  { label: 'Front Load', query: 'Front Load' },
];

export function HomeSearchSection({
  searchQuery,
  resultCount,
  onSearchInput,
  onSubmit,
}: HomeSearchSectionProps) {
  return (
    <section className="relative overflow-hidden border-b border-blue-700/10 bg-blue-700/[0.045] py-7 sm:py-9">
      <div className="pointer-events-none absolute -left-20 top-1/2 hidden h-56 w-56 -translate-y-1/2 rounded-full border-[18px] border-blue-700/10 bg-blue-700/[0.035] lg:block">
        <div className="absolute left-1/2 top-6 h-6 w-24 -translate-x-1/2 rounded-lg bg-white/70" />
        <div className="absolute left-1/2 top-1/2 h-28 w-28 -translate-x-1/2 -translate-y-1/2 rounded-full border-[11px] border-white/70 bg-blue-700/[0.045]" />
      </div>

      <div className="pointer-events-none absolute -right-20 top-1/2 hidden h-56 w-56 -translate-y-1/2 rounded-full border-[18px] border-blue-700/10 bg-blue-700/[0.035] lg:block">
        <div className="absolute left-1/2 top-6 h-6 w-24 -translate-x-1/2 rounded-lg bg-white/70" />
        <div className="absolute left-1/2 top-1/2 h-28 w-28 -translate-x-1/2 -translate-y-1/2 rounded-full border-[11px] border-white/70 bg-blue-700/[0.045]" />
      </div>

      <div className="container relative z-10 mx-auto px-4">
        <div className="mx-auto max-w-3xl text-center">
          <Sparkles className="mx-auto mb-1.5 h-5 w-5 text-blue-700" aria-hidden="true" />
          <h2 className="text-2xl font-black tracking-tight text-slate-950 sm:text-3xl lg:text-4xl">
            Find Your Perfect Appliance
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-slate-600 sm:text-base">
            Discover quality washers, dryers, and more from brands you can trust.
          </p>

          <form onSubmit={onSubmit} className="mt-5 sm:mt-6">
            <div className="flex flex-col gap-2 rounded-2xl border border-blue-700/20 bg-white p-1.5 shadow-[0_14px_35px_rgba(29,78,216,0.12)] sm:flex-row sm:items-center sm:rounded-full">
              <div className="relative min-w-0 flex-1">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-blue-700" aria-hidden="true" />
                <input
                  type="search"
                  placeholder="Search for washers, dryers, brands..."
                  value={searchQuery}
                  onChange={(event) => onSearchInput(event.target.value)}
                  onInput={(event) => onSearchInput(event.currentTarget.value)}
                  className="w-full rounded-xl border-0 bg-transparent py-3 pl-12 pr-4 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:ring-0 sm:rounded-full sm:py-3.5 sm:text-base"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
              </div>
              <button
                type="submit"
                className="rounded-xl bg-blue-700 px-6 py-3 text-sm font-extrabold text-white shadow-md transition hover:bg-blue-700/90 focus:outline-none focus:ring-2 focus:ring-blue-700/30 focus:ring-offset-2 sm:rounded-full sm:px-8 sm:py-3.5 sm:text-base"
              >
                Search
              </button>
            </div>
          </form>

          <div className="mt-4 flex flex-col items-center justify-center gap-2 text-xs sm:flex-row sm:flex-wrap sm:text-sm">
            <div className="flex items-center gap-2 font-bold text-slate-700">
              <Flame className="h-4 w-4 text-blue-700" aria-hidden="true" />
              Popular searches:
            </div>
            <div className="flex flex-wrap justify-center gap-1.5">
              {popularSearches.map((item) => (
                <Link
                  key={item.label}
                  to={`/shop?search=${encodeURIComponent(item.query)}`}
                  className="rounded-full border border-blue-700/20 bg-white/80 px-3 py-1.5 font-semibold text-blue-700 transition hover:border-blue-700/40 hover:bg-blue-700/[0.06]"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          {searchQuery && (
            <div className="mt-3 text-center">
              {resultCount > 0 ? (
                <p className="text-xs text-slate-600 sm:text-sm">
                  Found {resultCount} product{resultCount !== 1 ? 's' : ''} matching “{searchQuery}”
                </p>
              ) : (
                <p className="text-xs text-slate-600 sm:text-sm">
                  No products found matching “{searchQuery}”. Try a different search term.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
